import React, { useState, useMemo, useEffect } from 'react';
import { Box, Paper } from '@mui/material';
import { toast } from 'react-hot-toast';
import useSWR from 'swr';

import { keywordApi } from 'src/api/keywordApi';

// Import component files
import KeywordHeader from './components/KeywordHeader';
import AddKeywordForm from './components/AddKeywordForm';
import KeywordSearchField from './components/KeywordSearchField';
import KeywordsList from './components/KeywordsList';

const fetcher = () => keywordApi.fetchKeywords();

const ManageKeywords = () => {
  const [keywords, setKeywords] = useState([]);
  const { data, error, mutate } = useSWR('keywords', fetcher);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    if (data?.length > 0) {
      setKeywords(data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast.error('Error fetching keywords:', error);
    }
  }, [error]);

  const handleAddKeyword = async (newKeyword) => {
    if (newKeyword.trim() && !keywords.some((k) => k.keyword === newKeyword.trim())) {
      try {
        setIsSubmitting(true);
        // Send the request to the server
        const response = await keywordApi.submitKeyword(newKeyword.trim(), 'blacklisted');
        if (!response.error) {
          toast.success('Keyword added successfully');
          mutate();
        } else {
          toast.error('Failed to add keyword');
        }
      } catch (addError) {
        toast.error('An error occurred while adding the keyword');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleRemoveKeyword = async (keywordToRemove) => {
    const keywordObj = keywords.find((k) => k.keyword === keywordToRemove);
    if (keywordObj) {
      try {
        setIsRemoving(true);
        // Send the request to the server
        const response = await keywordApi.deleteKeyword(keywordObj.id);
        if (!response.error) {
          // Update the local state immediately after successful deletion
          setKeywords((prevKeywords) => prevKeywords.filter((k) => k.id !== keywordObj.id));
          toast.success('Keyword removed successfully');
        } else {
          toast.error('Failed to remove keyword');
        }
        mutate();
      } catch (removeError) {
        toast.error('An error occurred while removing the keyword');
      } finally {
        setIsRemoving(false);
      }
    }
  };

  const filteredKeywords = useMemo(() => {
    return keywords.filter((keyword) => keyword.keyword.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [keywords, searchQuery]);

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Paper sx={{ p: 3, mt: { xs: 6, md: 0 } }}>
        {/* Header */}
        <KeywordHeader keywordCount={keywords.length} />

        {/* Add Keyword Form */}
        <AddKeywordForm onAddKeyword={handleAddKeyword} isSubmitting={isSubmitting} />

        {/* Search Field */}
        <KeywordSearchField searchQuery={searchQuery} onSearchChange={setSearchQuery} />

        {/* Keywords List */}
        <KeywordsList keywords={filteredKeywords} searchQuery={searchQuery} onRemoveKeyword={handleRemoveKeyword} isRemoving={isRemoving} />
      </Paper>
    </Box>
  );
};

export default ManageKeywords;
